import { Link } from "react-router-dom";
import { Sparkles, AlertCircle, Shield } from "lucide-react";
import { useLang } from "@/lib/lang-context";
import { useAuth } from "@/lib/auth-context";
import { useQuota } from "@/lib/quota-context";

/**
 * Thin banner that sits above the input panel on the Home page.
 *
 * Renders one of four states based on the current quota tier:
 *   • Guest with budget        → "1 free detailed analysis — register to get 3"
 *   • Guest exhausted          → "Register for free to unlock more full reports"
 *   • Free user with budget    → "You still have N free detailed analyses"
 *   • Free user exhausted      → "You have used up your free reports — €1.99 or Premium"
 *   • Premium                  → green pill "Premium · Unlimited"
 *
 * Premium / credit-rich users get a low-noise confirmation pill so they
 * always know they're in good standing.
 */
const QuotaBanner = ({ onOpenPaywall }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const { quota } = useQuota();

  if (!quota) return null;

  const remaining = quota.remaining_free ?? 0;
  const tier = quota.tier;
  const credits = quota.credits ?? 0;

  // Admin (site owner) — discrete dark pill so you know the bypass is on.
  if (tier === "admin") {
    return (
      <div
        data-testid="quota-banner"
        data-tier="admin"
        className="bg-[#282C2A] text-white px-4 py-2.5 text-xs font-mono uppercase tracking-widest flex items-center justify-center gap-2"
      >
        <Shield size={13} strokeWidth={1.75} />
        Admin · Analisi illimitate
      </div>
    );
  }

  // Premium banner — small + green, never alarming.
  if (tier === "premium") {
    return (
      <div
        data-testid="quota-banner"
        data-tier="premium"
        className="bg-[#4A5D23] text-white px-4 py-2.5 text-xs font-mono uppercase tracking-widest flex items-center justify-center gap-2"
      >
        <Sparkles size={13} strokeWidth={1.75} />
        {t("quota.premiumActive")}
      </div>
    );
  }

  // Guest + still has free report.
  if (tier === "guest" && remaining > 0) {
    return (
      <div
        data-testid="quota-banner"
        data-tier="guest-ok"
        className="bg-[#F2EFE6] border-b border-[#E6E4DD] px-4 py-2.5 text-xs flex items-center justify-center gap-3 flex-wrap text-[#282C2A]"
      >
        <span data-testid="quota-banner-message">
          <strong className="font-semibold">{t("quota.guestRemaining")}</strong>
          {" · "}
          {t("quota.guestRegisterPrompt")}
        </span>
        <Link
          to="/login"
          data-testid="quota-banner-cta"
          className="font-mono text-[10px] uppercase tracking-widest px-3 py-1 border border-[#282C2A] hover:bg-[#282C2A] hover:text-white transition-colors"
        >
          {t("quota.registerCta")}
        </Link>
      </div>
    );
  }

  // Guest exhausted.
  if (tier === "guest") {
    return (
      <div
        data-testid="quota-banner"
        data-tier="guest-exhausted"
        className="bg-[#FFF1E6] border-b border-[#E0BBA0] px-4 py-2.5 text-xs flex items-center justify-center gap-3 flex-wrap text-[#7A3A12]"
      >
        <AlertCircle size={13} strokeWidth={1.75} />
        <span data-testid="quota-banner-message">{t("quota.guestExhausted")}</span>
        <Link
          to="/login"
          data-testid="quota-banner-cta"
          className="font-mono text-[10px] uppercase tracking-widest px-3 py-1 bg-[#4A5D23] text-white hover:bg-[#3B4A1C] transition-colors"
        >
          {t("quota.registerCta")}
        </Link>
      </div>
    );
  }

  // Logged-in user with free reports remaining.
  if (tier === "free" && remaining > 0) {
    return (
      <div
        data-testid="quota-banner"
        data-tier="free-ok"
        className="bg-[#F2EFE6] border-b border-[#E6E4DD] px-4 py-2.5 text-xs text-[#282C2A] text-center"
      >
        <span data-testid="quota-banner-message">
          {t("quota.userRemaining").replace("{n}", remaining)}
        </span>
      </div>
    );
  }

  // Logged-in user with extra credits (no free reports left).
  if (tier === "credit" || (user && credits > 0 && remaining === 0)) {
    return (
      <div
        data-testid="quota-banner"
        data-tier="credit"
        className="bg-[#F2EFE6] border-b border-[#E6E4DD] px-4 py-2.5 text-xs text-[#282C2A] flex items-center justify-center gap-3 flex-wrap"
      >
        <span data-testid="quota-banner-message">
          {t("quota.creditsRemaining").replace("{n}", credits)}
        </span>
        <button
          type="button"
          onClick={onOpenPaywall}
          data-testid="quota-banner-cta"
          className="font-mono text-[10px] uppercase tracking-widest px-3 py-1 border border-[#282C2A] hover:bg-[#282C2A] hover:text-white transition-colors"
        >
          {t("quota.upgradeCta")}
        </button>
      </div>
    );
  }

  // Logged-in user exhausted (no credits, no free left).
  return (
    <div
      data-testid="quota-banner"
      data-tier="free-exhausted"
      className="bg-[#FFF1E6] border-b border-[#E0BBA0] px-4 py-2.5 text-xs flex items-center justify-center gap-3 flex-wrap text-[#7A3A12]"
    >
      <AlertCircle size={13} strokeWidth={1.75} />
      <span data-testid="quota-banner-message">{t("quota.userExhausted")}</span>
      <button
        type="button"
        onClick={onOpenPaywall}
        data-testid="quota-banner-cta"
        className="font-mono text-[10px] uppercase tracking-widest px-3 py-1 bg-[#4A5D23] text-white hover:bg-[#3B4A1C] transition-colors"
      >
        {t("quota.unlockCta")}
      </button>
    </div>
  );
};

export default QuotaBanner;
